/* CUSTOM_AD_VISITOR_BY_CLAUDE — UTM detection + ad visitor web components */

(function () {
  const SESSION_KEY = 'bsf_ad_visitor';
  const DISMISS_KEY = 'bsf_ad_banner_dismissed';

  /**
   * Detect ad visitor via UTM params, persist to sessionStorage,
   * and strip UTM params from URL.
   */
  function detectAdVisitor() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_source') === 'google' && params.get('utm_medium') === 'cpc') {
      sessionStorage.setItem(SESSION_KEY, '1');
      params.delete('utm_source');
      params.delete('utm_medium');
      params.delete('utm_campaign');
      params.delete('utm_term');
      params.delete('utm_content');
      var clean = params.toString();
      var cleanUrl = window.location.pathname + (clean ? '?' + clean : '') + window.location.hash;
      history.replaceState(null, '', cleanUrl);
    }
    return sessionStorage.getItem(SESSION_KEY) === '1';
  }

  var isAdVisitor = detectAdVisitor();

  /* ============================================
     Sticky bottom banner (global, all pages)
     ============================================ */

  if (!customElements.get('ad-visitor-banner')) {
    class AdVisitorBanner extends HTMLElement {
      connectedCallback() {
        if (!isAdVisitor || sessionStorage.getItem(DISMISS_KEY) === '1') return;

        if (window.matchMedia('(min-width: 769px)').matches && document.querySelector('ad-visitor-cta')) {
          return;
        }

        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('inert'); // CUSTOM_COLLECTIONSBOT_BY_CLAUDE — paired with dismiss()

        var dismissBtn = this.querySelector('.ad-visitor-banner__dismiss');
        if (dismissBtn) {
          dismissBtn.addEventListener('click', this.dismiss.bind(this));
        }
      }

      dismiss() {
        sessionStorage.setItem(DISMISS_KEY, '1');
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', ''); // CUSTOM_COLLECTIONSBOT_BY_CLAUDE — paired with connectedCallback()
      }
    }

    customElements.define('ad-visitor-banner', AdVisitorBanner);
  }

  /* ============================================
     Collection page CTAs (samples + quote)
     ============================================ */

  if (!customElements.get('ad-visitor-cta')) {
    class AdVisitorCta extends HTMLElement {
      connectedCallback() {
        /* CUSTOM_ECORE_BY_CLAUDE — Removed UTM gate; in-page CTA shows for all visitors on collections that include this section. The bottom sticky banner above remains UTM-gated. */
        this.style.display = '';
        this.classList.add('is-visible');

        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE 2026-08-02 — OPT OUT OF PINNING.
           On a collection page this banner fixing itself under the header is the whole point: the
           product grid scrolls underneath it. On a page whose main content is a single tall
           INTERACTIVE surface — the Weight Designer — it is the opposite of the point: once you
           scroll past it, it pins and then covers the top of the designer, step tabs included, for
           as long as you are using the tool. There is no scroll position that dismisses it.
           `data-no-pin` leaves everything else intact (the banner still reveals itself, still
           renders its trust row); it only declines the fixed behaviour, so the strip behaves like
           an ordinary section that scrolls away.
           ⚠ Default is UNCHANGED — absent attribute means pin, exactly as before, so no collection
           page is touched by this. */
        if (window.matchMedia('(min-width: 769px)').matches
            && this.dataset.noPin !== 'true') {
          this.initSticky();
        }
      }

      initSticky() {
        this.sentinel = document.createElement('div');
        this.sentinel.style.height = '1px';
        this.sentinel.style.visibility = 'hidden';
        this.sentinel.style.pointerEvents = 'none';
        this.parentElement.insertBefore(this.sentinel, this);

        this.placeholder = document.createElement('div');
        this.placeholder.style.display = 'none';
        this.after(this.placeholder);

        this.header = document.querySelector('.cc-header--sticky') || document.querySelector('header');
        this.isFixed = false;
        this.rafId = null;

        var self = this;
        var onScroll = function () {
          var headerBottom = self.header.getBoundingClientRect().bottom;
          var sentinelTop = self.sentinel.getBoundingClientRect().top;

          if (sentinelTop <= headerBottom && !self.isFixed) {
            self.placeholder.style.height = self.offsetHeight + 'px';
            self.placeholder.style.display = 'block';
            self.classList.add('is-fixed');
            self.style.top = headerBottom + 'px';
            self.isFixed = true;
            // Expose the pinned banner's live height so other fixed/sticky elements below it
            // (e.g. the facet filter sidebar's scroll-direction catch-up) can avoid landing
            // underneath it — see assets/sticky-scroll-direction.js.
            // Measured ONCE at the pin transition (the pinned height is constant until resize) —
            // publishBottom reuses it, so the steady-state scroll frames never read layout after
            // the style.top write and don't force a reflow every frame.
            self.pinnedHeight = self.offsetHeight;
            document.documentElement.style.setProperty('--ad-visitor-cta-height', self.pinnedHeight + 'px');
            self.publishBottom(headerBottom);
          } else if (sentinelTop > headerBottom && self.isFixed) {
            self.classList.remove('is-fixed');
            self.style.top = '';
            self.placeholder.style.display = 'none';
            self.isFixed = false;
            document.documentElement.style.setProperty('--ad-visitor-cta-height', '0px');
            // Let anything anchored to the banner's bottom (the sticky products-toolbar) fall back
            // to its own header-based offset now that the banner is no longer pinned.
            document.documentElement.style.removeProperty('--ad-visitor-cta-bottom');
          } else if (self.isFixed) {
            self.style.top = headerBottom + 'px';
            self.publishBottom(headerBottom);
          }
        };

        this.scrollListener = function () {
          if (self.rafId) return;
          self.rafId = requestAnimationFrame(function () {
            onScroll();
            self.rafId = null;
          });
        };

        window.addEventListener('scroll', this.scrollListener, { passive: true });

        // The cached pinned height goes stale if the viewport resizes while pinned
        // (text can rewrap) — re-measure once per resize, not per scroll frame.
        this.resizeListener = function () {
          if (!self.isFixed) { self.pinnedHeight = null; return; }
          self.pinnedHeight = self.offsetHeight;
          document.documentElement.style.setProperty('--ad-visitor-cta-height', self.pinnedHeight + 'px');
          self.scrollListener();
        };
        window.addEventListener('resize', this.resizeListener);

        onScroll();
      }

      // Publish the banner's live bottom edge (in viewport px) every frame it moves. The sticky
      // products-toolbar anchors its `top` to this var (assets/facet-filters.css) instead of
      // recomputing header-end + banner-height on its own schedule. Because this is written in the
      // SAME frame the banner repositions itself, the toolbar stays locked flush to the banner even
      // while the auto-hiding site header is mid-animation — no cross-schedule catch-up lag.
      publishBottom(headerBottom) {
        var h = this.pinnedHeight != null ? this.pinnedHeight : this.offsetHeight;
        document.documentElement.style.setProperty(
          '--ad-visitor-cta-bottom',
          `${headerBottom + h}px`
        );
      }

      disconnectedCallback() {
        if (this.scrollListener) {
          window.removeEventListener('scroll', this.scrollListener);
        }
        if (this.resizeListener) {
          window.removeEventListener('resize', this.resizeListener);
        }
      }
    }

    customElements.define('ad-visitor-cta', AdVisitorCta);
  }
})();

/* END CUSTOM_AD_VISITOR_BY_CLAUDE */
